import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageCircle, Loader2, Trash2 } from "lucide-react";

export function JobChat({ projectId, scheduleId, compact = false }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const isManager = user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin";

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, scheduleId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      let url = `${API}/job-chat/${projectId}`;
      if (scheduleId) url += `?schedule_id=${scheduleId}`;
      const res = await axios.get(url);
      setMessages(res.data || []);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/job-chat`, {
        project_id: projectId,
        schedule_id: scheduleId,
        content: newMsg.trim(),
      });
      setNewMsg("");
      fetchMessages();
    } catch (err) {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (msgId) => {
    try {
      await axios.delete(`${API}/job-chat/${msgId}`);
      fetchMessages();
    } catch (err) {
      toast.error("Cannot delete");
    }
  };

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  return (
    <div className={`flex flex-col ${compact ? "h-64" : "h-80"}`} data-testid="job-chat">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50 shrink-0">
        <MessageCircle className="w-4 h-4 text-indigo-600" />
        <span className="text-sm font-medium">Job Chat</span>
        <Badge variant="outline" className="text-xs ml-auto">{messages.length}</Badge>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-slate-400 mt-8">No messages yet. Start the conversation!</div>
        ) : (
          messages.map(msg => {
            const isOwn = msg.user_id === user?.user_id;
            const isMgrMsg = msg.user_role === "owner" || msg.user_role === "project_manager" || msg.user_role === "office_admin";
            return (
              <div key={msg.message_id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${isOwn ? "order-2" : ""}`}>
                  <div className={`rounded-xl px-3 py-2 ${isOwn ? "bg-indigo-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                    {!isOwn && (
                      <div className="flex items-center gap-1 mb-1">
                        <span className={`text-[10px] font-semibold ${isOwn ? "text-indigo-200" : "text-slate-500"}`}>{msg.author?.name || msg.user_name}</span>
                        {isMgrMsg && <Badge className="text-[8px] px-1 py-0 bg-purple-100 text-purple-600">Mgr</Badge>}
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? "justify-end" : ""}`}>
                    <span className="text-[10px] text-slate-400">{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {(isOwn || isManager) && (
                      <button onClick={() => handleDelete(msg.message_id)} className="text-slate-300 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 px-3 py-2 border-t bg-white shrink-0">
        <Input
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          placeholder="Type a message..."
          className="text-sm"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          data-testid="chat-input"
        />
        <Button size="sm" onClick={handleSend} disabled={sending || !newMsg.trim()} data-testid="chat-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
